import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { prefixKey } from '@/utils/storagePrefix';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { fetchPermittedPeople } from '@/services/enteringForService';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PermittedPerson {
  id: string;
  name: string;
  /** Live mode only: the backend employee id of the person (resource or contractor). */
  employeeId?: number;
  department?: string | null;
  /** Resource Manager resource id — set for contractors, whose bookings key on it. */
  resourceId?: number | null;
  isContractor?: boolean;
  requiresApproval?: boolean;
  /** The person's assigned role, used to default the Role on their timesheet lines. */
  roleId?: number;
  roleName?: string;
}

// ── Mock data ──────────────────────────────────────────────────────────────────

/** The full list of people this user is permitted to enter time for. */
export const PERMITTED_PEOPLE: PermittedPerson[] = [
  { id: 'john-davis', name: 'John Davis' },
  { id: 'lisa-ray', name: 'Lisa Ray' },
  { id: 'alex-chen', name: 'Alex Chen' },
];

/** Mock timesheet projects shown when entering for John Davis. */
export const JOHN_DAVIS_PROJECTS = [
  { id: 'proj-alpha', name: 'Project Alpha', client: 'Barclays Bank' },
  { id: 'proj-beta', name: 'Project Beta', client: 'HSBC Holdings' },
];

// ── Storage ───────────────────────────────────────────────────────────────────

const STORAGE_KEY = prefixKey('enteringForPersonId');

function getStoredPersonId(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return null;
    if (stored === 'myself') return null;
    return stored;
  } catch {
    return null;
  }
}

// ── Context ───────────────────────────────────────────────────────────────────

interface EnteringForContextType {
  /** Full list of people this user may enter time for. */
  permittedPeople: PermittedPerson[];
  /** null → entering for myself; string → the selected person's id. */
  selectedPersonId: string | null;
  /** Resolved object for the selected person, or null when entering for myself. */
  selectedPerson: PermittedPerson | null;
  /** Convenience flag: true when a person other than "myself" is selected. */
  isEnteringForOther: boolean;
  /**
   * Live mode only: the backend employee id whose timesheet is being entered, or null when
   * entering for myself or in demo mode (where the mock grid is shown instead).
   */
  selectedEmployeeId: number | null;
  /** Update the selection and persist it in localStorage. */
  setSelectedPersonId: (id: string | null) => void;
}

const EnteringForContext = createContext<EnteringForContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export function EnteringForProvider({ children }: { children: ReactNode }) {
  const { mode, isLive } = useScreenDataMode('timesheet');
  const [selectedPersonId, setSelectedPersonIdState] = useState<string | null>(getStoredPersonId);
  const [livePeople, setLivePeople] = useState<PermittedPerson[]>([]);

  const setSelectedPersonId = useCallback((id: string | null) => {
    setSelectedPersonIdState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id ?? 'myself');
    } catch {
      // localStorage unavailable — state still updates in memory
    }
  }, []);

  // Demo and live people have different ids, so a selection never carries across a mode switch.
  const previousModeRef = useRef(mode);
  useEffect(() => {
    if (previousModeRef.current === mode) return;
    previousModeRef.current = mode;
    setSelectedPersonId(null);
  }, [mode, setSelectedPersonId]);

  useEffect(() => {
    if (!isLive) {
      setLivePeople([]);
      return;
    }

    let cancelled = false;
    fetchPermittedPeople()
      .then(people => {
        if (!cancelled) setLivePeople(people);
      })
      .catch(error => {
        console.error('Failed to load people you can enter time for:', error);
        if (!cancelled) setLivePeople([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isLive]);

  const permittedPeople = isLive ? livePeople : PERMITTED_PEOPLE;

  const selectedPerson = selectedPersonId
    ? (permittedPeople.find(p => p.id === selectedPersonId) ?? null)
    : null;

  const isEnteringForOther = selectedPerson !== null;
  const selectedEmployeeId = isLive ? (selectedPerson?.employeeId ?? null) : null;

  return (
    <EnteringForContext.Provider
      value={{
        permittedPeople,
        selectedPersonId,
        selectedPerson,
        isEnteringForOther,
        selectedEmployeeId,
        setSelectedPersonId,
      }}
    >
      {children}
    </EnteringForContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useEnteringFor(): EnteringForContextType {
  const ctx = useContext(EnteringForContext);
  if (!ctx) {
    throw new Error('useEnteringFor must be used within an EnteringForProvider');
  }
  return ctx;
}
